// หนังสือเตือน. Pure (no I/O).
//
// 🚨 A FLAT LIST BY THE OWNER'S DECISION — no ครั้งที่ 1/2/3 level and no one-year
// expiry clock. The consequence has to be respected everywhere: "three strikes" is
// NOT computable from this data, so NO screen may imply a strike count. No red
// counter beside a name, no "2 active warnings" chip. Count the rows if you like;
// do not dress the number up as a verdict the clinic has not actually made.
//
// With no printed letter either, the record's evidentiary weight rests on exactly
// two things, and both are in the schema: `acknowledged_at` (the subject's own
// authenticated click, timestamped and IP-stamped server-side) and `file[]` (a scan
// of whatever paper HR does sign).

export const WARNING_STATE = {
  pending: 'รอรับทราบ',
  acknowledged: 'รับทราบแล้ว',
  voided: 'ยกเลิกแล้ว',
}

export const WARNING_TONE = {
  pending: 'bg-amber-50 text-amber-700 ring-amber-200',
  acknowledged: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  voided: 'bg-slate-100 text-slate-500 ring-slate-200',
}

// A voided warning reads as voided FIRST, whatever else is true of it — an
// acknowledged-then-withdrawn letter must never render as simply "รับทราบแล้ว".
export function warningState(w) {
  if (w?.voided_at) return 'voided'
  if (w?.acknowledged_at) return 'acknowledged'
  return 'pending'
}

export const warningLabel = (w) => WARNING_STATE[warningState(w)]
export const warningTone = (w) => WARNING_TONE[warningState(w)]

// Still waiting on the employee. A voided warning does NOT count — it must not keep
// the notification dot lit on a document HR has already withdrawn.
export const needsAck = (w) => !w?.acknowledged_at && !w?.voided_at

// Editing is refused once the employee has acknowledged: rewriting the text of a
// document somebody has already signed off on is what destroys its value in a
// dispute. Xano enforces this; the UI just stops offering it.
export const canEdit = (w) => !w?.acknowledged_at && !w?.voided_at
export const canVoid = (w) => !w?.voided_at

// ค.ศ. dd/mm/yyyy — the format every HR screen in this app uses. (fmtDateTH's
// Buddhist era is for generated documents only; see project.md §Dates.)
export function fmtDay(v) {
  if (!v) return '—'
  const d = typeof v === 'number' ? new Date(v) : new Date(`${String(v).slice(0, 10)}T00:00:00Z`)
  if (Number.isNaN(d.getTime())) return '—'
  const p = (n) => String(n).padStart(2, '0')
  return `${p(d.getUTCDate())}/${p(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}`
}

// Timestamps are epoch ms and are displayed in Bangkok wall-clock. The
// acknowledgement time is evidence, so it prints to the minute, not "2 days ago".
export function fmtStamp(ms) {
  const t = Number(ms)
  if (!Number.isFinite(t) || t <= 0) return '—'
  const d = new Date(t + 7 * 3600 * 1000)
  const p = (n) => String(n).padStart(2, '0')
  return `${p(d.getUTCDate())}/${p(d.getUTCMonth() + 1)}/${d.getUTCFullYear()} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`
}

export const ACK_CONFIRM_TH =
  'การกดรับทราบจะบันทึกวันเวลาไว้เป็นหลักฐานว่าท่านได้อ่านหนังสือฉบับนี้แล้ว ' +
  'การรับทราบไม่ได้แปลว่าท่านเห็นด้วย — หากไม่เห็นด้วย สามารถบันทึกความเห็นของท่านไว้ในช่องด้านล่างได้'

// Group a flat list by employee_id, newest first within each — the HR register's
// shape. Sorting is by issue_date then id, because issue_date is nullable on old
// rows and id is the only key that always exists.
export function groupByEmployee(rows) {
  const m = new Map()
  for (const w of Array.isArray(rows) ? rows : []) {
    const id = Number(w?.employee_id) || 0
    if (!id) continue
    if (!m.has(id)) m.set(id, [])
    m.get(id).push(w)
  }
  for (const list of m.values()) {
    list.sort((a, b) => String(b.issue_date || '').localeCompare(String(a.issue_date || '')) || (b.id - a.id))
  }
  return m
}
